// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useNavigate } from '@tanstack/react-router';

// material ui
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

// api
import {
  type MeterTypeEnum,
  useCreateEforsyningMetadataRowIntegrationsEforsyningMetadataPostMutation,
} from '@/api/generated/dataIntegrations/endpoints/dataIntegrations';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';

// components
import { supplierOptions } from '@/features/eforsyning-integration/eforsyningSupplierOptions';
import { showSnackbar } from '@/redux/snackbarSlice';

export const Route = createFileRoute(
  '/$accountId/_user/eforsyning-integration/create',
)({
  component: RouteComponent,
});

const meterTypeOptions: MeterTypeEnum[] = ['HeatMeter', 'WaterMeter'];

type SupplierOption = {
  value: string;
  label: string;
};

const supplierAutocompleteOptions: SupplierOption[] = supplierOptions.map(
  (supplier) => ({
    value: String(supplier.supplierId ?? '').trim(),
    label: String(supplier.name ?? supplier.supplierId ?? '').trim(),
  }),
);

type FormValues = {
  username: string;
  password: string;
  active: boolean;
  meterType: MeterTypeEnum;
  supplierId: string;
  reCalcName: string;
};

function RouteComponent() {
  const { t } = useTranslation(['eforsyning-integration', 'common']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountId } = Route.useParams();
  const accountIdAsNumber = Number(accountId);
  const isAccountIdValid = Number.isFinite(accountIdAsNumber);

  const [createMetadataRow, { isLoading: isCreating }] =
    useCreateEforsyningMetadataRowIntegrationsEforsyningMetadataPostMutation();

  const [formValues, setFormValues] = useState<FormValues>({
    username: '',
    password: '',
    active: true,
    meterType: 'HeatMeter',
    supplierId: '',
    reCalcName: '',
  });

  const selectedSupplierOption =
    supplierAutocompleteOptions.find(
      (option) => option.value === formValues.supplierId,
    ) ??
    (formValues.supplierId
      ? {
          value: formValues.supplierId,
          label: formValues.supplierId,
        }
      : null);

  const handleFieldChange = <K extends keyof FormValues>(
    field: K,
    value: FormValues[K],
  ) => {
    setFormValues((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!isAccountIdValid) {
      dispatch(
        showSnackbar({
          message: t('accountIdInvalid', {
            defaultValue: 'The account id is invalid.',
          }),
          severity: 'error',
        }),
      );
      return;
    }

    if (
      !formValues.username.trim() ||
      !formValues.password.trim() ||
      !formValues.supplierId.trim() ||
      !formValues.reCalcName.trim()
    ) {
      dispatch(
        showSnackbar({
          message: t('createValidationError', {
            defaultValue: 'Please fill in all required fields.',
          }),
          severity: 'error',
        }),
      );
      return;
    }

    const result = await createMetadataRow({
      eforsyningMetadataCreate: {
        PartitionKey: accountIdAsNumber,
        RowKey: formValues.username.trim(),
        Password: formValues.password,
        Active: formValues.active,
        MeterType: formValues.meterType,
        SupplierId: formValues.supplierId.trim(),
        ReCalcName: formValues.reCalcName.trim(),
        Address_Backup: formValues.reCalcName.trim(),
        ReCalcAccountId: accountIdAsNumber,
      },
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('createSuccess', {
            defaultValue: 'Eforsyning integration created successfully.',
          }),
          severity: 'success',
        }),
      );

      navigate({ to: '../' });
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <PageContainer
      maxWidth="xl"
      sx={{ py: 2 }}
      data-testid="eforsyning-metadata-create-container"
    >
      <PageHeader
        title={t('createTitle', {
          defaultValue: 'Create Eforsyning integration',
        })}
      />

      <Stack
        spacing={2}
        mb={2}
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <Card>
          <CardHeader
            title={t('general', {
              defaultValue: 'Eforsyning metadata',
            })}
            sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
            slotProps={{
              title: { variant: 'subtitle1' }
            }}
          />
          <CardContent>
            <Stack spacing={2}>
              {!isAccountIdValid ? (
                <Alert severity="error">
                  {t('accountIdUrlInvalid', {
                    defaultValue: 'The account id in the URL is invalid.',
                  })}
                </Alert>
              ) : null}

              <Box
                display="grid"
                gap={2}
                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
              >
                <TextField
                  label={t('username', {
                    defaultValue: 'Username',
                  })}
                  value={formValues.username}
                  onChange={(event) =>
                    handleFieldChange('username', event.target.value)
                  }
                  required
                  fullWidth
                />
                <TextField
                  label={t('password', {
                    defaultValue: 'Password',
                  })}
                  value={formValues.password}
                  onChange={(event) =>
                    handleFieldChange('password', event.target.value)
                  }
                  required
                  type="password"
                  fullWidth
                />
              </Box>

              <Box
                display="grid"
                gap={2}
                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
              >
                <Autocomplete
                  options={supplierAutocompleteOptions}
                  value={selectedSupplierOption}
                  onChange={(_, option) =>
                    handleFieldChange('supplierId', option?.value ?? '')
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  getOptionLabel={(option) => `${option.label}`}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('supplier', {
                        defaultValue: 'Supplier',
                      })}
                      required
                    />
                  )}
                />
                <TextField
                  label={t('reCalcName', {
                    defaultValue: 'ReCalcName',
                  })}
                  value={formValues.reCalcName}
                  onChange={(event) =>
                    handleFieldChange('reCalcName', event.target.value)
                  }
                  required
                  fullWidth
                />
              </Box>

              <Box
                display="grid"
                gap={2}
                gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
              >
                <TextField
                  select
                  label={t('meterType', {
                    defaultValue: 'MeterType',
                  })}
                  value={formValues.meterType}
                  onChange={(event) =>
                    handleFieldChange(
                      'meterType',
                      event.target.value as MeterTypeEnum,
                    )
                  }
                  required
                  fullWidth
                >
                  {meterTypeOptions.map((meterType) => (
                    <MenuItem key={meterType} value={meterType}>
                      {t(`meterTypes.${meterType}`, {
                        defaultValue: meterType,
                      })}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Stack>
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate({ to: '../' })}>
              {t('actions.cancel', { ns: 'common', defaultValue: 'Cancel' })}
            </Button>
            <Button variant="contained" type="submit" disabled={isCreating}>
              {isCreating
                ? t('creating', {
                    defaultValue: 'Creating...',
                  })
                : t('actions.create', { ns: 'common', defaultValue: 'Create' })}
            </Button>
          </CardActions>
        </Card>
      </Stack>
    </PageContainer>
  );
}
